import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { hasAnyPermission, normalizePermissions, PermissionUser } from './lib/permissions';
import { trackPageView, trackReferralLinkClick } from './lib/analytics';
import { brand } from './config/brand';
import OrderLanding from './pages/OrderLanding';
import OrderConfirmation from './pages/OrderConfirmation';
import PaymentProofUpload from './pages/PaymentProofUpload';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminOrders from './pages/AdminOrders';
import AdminOrderDetail from './pages/AdminOrderDetail';
import AdminCustomers from './pages/AdminCustomers';
import AdminReferrals from './pages/AdminReferrals';
import AdminReferralDetail from './pages/AdminReferralDetail';
import AdminReferralTree from './pages/AdminReferralTree';
import AdminReferralRewards from './pages/AdminReferralRewards';

import AdminBroadcast from './pages/AdminBroadcast';
import AdminBroadcastDetail from './pages/AdminBroadcastDetail';
import AdminProducts from './pages/AdminProducts';

import AdminMediaLibrary from './pages/AdminMediaLibrary';
import AdminUsers from './pages/AdminUsers';
import AdminEstates from './pages/AdminEstates';
import AdminSettings from './pages/AdminSettings';
import AdminAuditLogs from './pages/AdminAuditLogs';
import AdminAhJimContent from './pages/AdminAhJimContent';
import BrandStory from './pages/BrandStory';
import AdminPageEditor from './pages/AdminPageEditor';
import AdminTestimonials from './pages/AdminTestimonials';
import AdminStudentStories from './pages/AdminStudentStories';
import AdminMediaCoverage from './pages/AdminMediaCoverage';
import AdminConversations from './pages/AdminConversations';
import AdminPackageConfigs from './pages/AdminPackageConfigs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import AdminWhatsAppCenter from './pages/AdminWhatsAppCenter';
import AdminAutoTags from './pages/AdminAutoTags';
import AdminCustomerJourneys from './pages/AdminCustomerJourneys';
import ReferralDashboard from './pages/ReferralDashboard';

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
  return null;
}

function normalizeReferralCode(raw: string): string {
  return raw
    .replace(/^[Rr]-?/, '')
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase();
}

function RefCapture() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  useEffect(() => {
    const gs = searchParams.get('gs');
    const ref = searchParams.get('ref');
    const r = searchParams.get('r');
    const rawCode = gs || ref || r;
    if (!rawCode) return;

    const code = normalizeReferralCode(rawCode);
    if (!code) return;

    localStorage.setItem('gs_referral_code', code);
    localStorage.setItem('gs2_referral_code', code);
    localStorage.setItem('gs_referral_ts', Date.now().toString());
    trackReferralLinkClick(code, location.pathname + location.search);

    const next = new URLSearchParams(searchParams);
    next.delete('gs');
    next.delete('ref');
    next.delete('r');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, location]);

  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Route guard component (permission-based; falls back to role for backward compatibility)
function AdminRoute({ children, requiredPermissions }: { children: React.ReactNode; requiredPermissions?: string[] }) {
  const token = localStorage.getItem('admin_token');
  const storedUser = localStorage.getItem('admin_user');
  const user: PermissionUser | null = storedUser ? JSON.parse(storedUser) : null;

  if (!token) {
    return <Navigate to="/admin" replace />;
  }

  if (requiredPermissions && requiredPermissions.length > 0) {
    const perms = normalizePermissions(user);
    const hasAccess = user?.role === 'super_admin' || perms.some(p => requiredPermissions.includes(p));
    if (!hasAccess) {
      // Redirect based on role
      if (user?.role === 'supplier') {
        return <Navigate to="/admin/products" replace />;
      }
      if (user?.role === 'estate_manager') {
        return <Navigate to="/admin/orders" replace />;
      }
      return <Navigate to="/admin/dashboard" replace />;
    }
  }

  return <>{children}</>;
}

const PRODUCTION_HOST = brand.domain;

function App() {
  // Ensure admin access always uses the production domain. Some restricted
  // networks (e.g. certain tablets/mobile carriers) block *.workers.dev.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hostname = window.location.hostname;
    if (hostname.endsWith('.workers.dev') && hostname !== PRODUCTION_HOST) {
      window.location.replace(
        `https://${PRODUCTION_HOST}${window.location.pathname}${window.location.search}`
      );
    }
  }, []);

  return (
    <Router>
      <PageViewTracker />
      <RefCapture />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<OrderLanding />} />
        <Route path="/campaign/:scenarioKey" element={<OrderLanding />} />
        <Route path="/ref/d/:token" element={<ReferralDashboard />} />
        <Route path="/order/success" element={<OrderConfirmation />} />
        <Route path="/payment-proof/:orderNum" element={<PaymentProofUpload />} />
        <Route path="/about" element={<BrandStory />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        
        {/* Admin Routes */}
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={
          <AdminRoute requiredPermissions={['dashboard:read']}>
            <AdminDashboard />
          </AdminRoute>
        } />
        <Route path="/admin/orders" element={
          <AdminRoute requiredPermissions={['orders:read']}>
            <AdminOrders />
          </AdminRoute>
        } />
        <Route path="/admin/orders/:id" element={
          <AdminRoute requiredPermissions={['orders:write']}>
            <AdminOrderDetail />
          </AdminRoute>
        } />
        <Route path="/admin/customers" element={
          <AdminRoute requiredPermissions={['customers:read']}>
            <AdminCustomers />
          </AdminRoute>
        } />
        <Route path="/admin/referrals" element={
          <AdminRoute requiredPermissions={['referrals:read']}>
            <AdminReferrals />
          </AdminRoute>
        } />
        <Route path="/admin/referrals/:phone" element={
          <AdminRoute requiredPermissions={['referrals:read']}>
            <AdminReferralDetail />
          </AdminRoute>
        } />
        <Route path="/admin/referrals/:phone/tree" element={
          <AdminRoute requiredPermissions={['referrals:read']}>
            <AdminReferralTree />
          </AdminRoute>
        } />
        <Route path="/admin/referral-rewards" element={
          <AdminRoute requiredPermissions={['referrals:write']}>
            <AdminReferralRewards />
          </AdminRoute>
        } />
        <Route path="/admin/products" element={
          <AdminRoute requiredPermissions={['products:read']}>
            <AdminProducts />
          </AdminRoute>
        } />

        <Route path="/admin/media-library" element={
          <AdminRoute requiredPermissions={['media:read']}>
            <AdminMediaLibrary />
          </AdminRoute>
        } />
        <Route path="/admin/broadcast" element={
          <AdminRoute requiredPermissions={['broadcast:send']}>
            <AdminBroadcast />
          </AdminRoute>
        } />
        <Route path="/admin/broadcast/:id" element={
          <AdminRoute requiredPermissions={['broadcast:send']}>
            <AdminBroadcastDetail />
          </AdminRoute>
        } />
        <Route path="/admin/users" element={
          <AdminRoute requiredPermissions={['users:read']}>
            <AdminUsers />
          </AdminRoute>
        } />
        <Route path="/admin/estates" element={
          <AdminRoute requiredPermissions={['estates:read']}>
            <AdminEstates />
          </AdminRoute>
        } />
        <Route path="/admin/settings" element={
          <AdminRoute requiredPermissions={['settings:read']}>
            <AdminSettings />
          </AdminRoute>
        } />
        <Route path="/admin/audit-logs" element={
          <AdminRoute requiredPermissions={['audit:read']}>
            <AdminAuditLogs />
          </AdminRoute>
        } />
        <Route path="/admin/ahjim-content" element={
          <AdminRoute requiredPermissions={['ahjim:read']}>
            <AdminAhJimContent />
          </AdminRoute>
        } />
        <Route path="/admin/pages/:slug" element={
          <AdminRoute requiredPermissions={['cms:read']}>
            <AdminPageEditor />
          </AdminRoute>
        } />
        <Route path="/admin/testimonials" element={
          <AdminRoute requiredPermissions={['cms:read']}>
            <AdminTestimonials />
          </AdminRoute>
        } />
        <Route path="/admin/stories" element={
          <AdminRoute requiredPermissions={['cms:read']}>
            <AdminStudentStories />
          </AdminRoute>
        } />
        <Route path="/admin/media-coverage" element={
          <AdminRoute requiredPermissions={['cms:read']}>
            <AdminMediaCoverage />
          </AdminRoute>
        } />
        <Route path="/admin/conversations" element={
          <AdminRoute requiredPermissions={['conversations:read']}>
            <AdminConversations />
          </AdminRoute>
        } />
        <Route path="/admin/package-configs" element={
          <AdminRoute requiredPermissions={['packages:read']}>
            <AdminPackageConfigs />
          </AdminRoute>
        } />
        <Route path="/admin/whatsapp-center" element={
          <AdminRoute requiredPermissions={['whatsapp:manage']}>
            <AdminWhatsAppCenter />
          </AdminRoute>
        } />
        <Route path="/admin/auto-tags" element={
          <AdminRoute requiredPermissions={['customer_journeys:manage']}>
            <AdminAutoTags />
          </AdminRoute>
        } />
        <Route path="/admin/customer-journeys" element={
          <AdminRoute requiredPermissions={['customer_journeys:manage']}>
            <AdminCustomerJourneys />
          </AdminRoute>
        } />
      </Routes>
    </Router>
  );
}

export default App;
